import { useMemo, useState, useEffect } from "react";
import type { TranscriptionSegment } from "@core/features/video";
import { Type } from "@themes/pollinator/ui/Type";
import VideoMeetingSubtitleItem from "./VideoMeetingSubtitleItem";

const DISPLAY_DURATION = 3000;

type Props = {
  segments: TranscriptionSegment[];
  currentSegment: TranscriptionSegment | null;
  isEnabled: boolean;
  isLoading?: boolean;
  error?: string | null;
  attendeeCount?: number;
};

export function VideoMeetingSubtitles({
  segments,
  currentSegment,
  isEnabled,
  isLoading,
  error,
  attendeeCount = 2,
}: Props) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isEnabled) return;

    const interval = setInterval(() => {
      setNow(Date.now());
    }, 100);
    return () => clearInterval(interval);
  }, [isEnabled]);

  const recentFinalSegments = useMemo(() => {
    if (!isEnabled) return [];

    return segments
      .filter(
        (seg) =>
          !seg.isPartial &&
          now - seg.startTime <= DISPLAY_DURATION &&
          seg.text.trim().length > 0
      )
      .sort((a, b) => b.startTime - a.startTime)
      .slice(0, 3);
  }, [segments, now, isEnabled]);

  const livePartialSegment =
    currentSegment?.isPartial && currentSegment.text.length > 0
      ? currentSegment
      : null;

  const hasContent =
    isEnabled && (recentFinalSegments.length > 0 || !!livePartialSegment);

  if (!isEnabled) return null;

  if (attendeeCount <= 1) return null;

  if (isLoading) {
    return (
      <div className="absolute bottom-24 left-4 z-50 w-[calc(100%-4rem)]">
        <div className="flex flex-col gap-4">
          <div
            role="status"
            className="bg-black/80 text-brand-50 px-4 py-2 rounded-lg"
          >
            <div className="flex items-center gap-2">
              <div
                aria-hidden="true"
                className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-50"
              ></div>
              <Type variant="body2">Starting transcription...</Type>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="absolute bottom-24 left-4 z-50 w-[calc(100%-4rem)]">
        <div className="flex flex-col gap-4">
          <div role="alert" className="bg-red-600/80 text-brand-50 px-4 py-2 rounded-lg">
            <Type variant="body2">Error: {error}</Type>
          </div>
        </div>
      </div>
    );
  }

  if (!hasContent) return null;

  // Decoupled accessibility: the visual list re-renders constantly (3s
  // window, partial-segment churn, fade-in/out) — feeding it directly
  // to a screen reader would be unusably chatty. Instead the visual
  // items are aria-hidden, and a single sr-only live region below
  // emits one polite announcement per *finalized* segment, formatted
  // for speech ("{speaker} says: {text}"). Partials are deliberately
  // excluded so SR users hear complete utterances, not keystroke-level
  // updates. See frontend/docs/video-experience.md §4 → Accessibility.
  const finalSegmentsForSr = segments
    .filter((seg) => !seg.isPartial && seg.text.trim().length > 0)
    .slice(-50);

  return (
    <section
      aria-label="Live captions"
      className="absolute bottom-24 left-4 z-50 w-[calc(100%-4rem)]"
    >
      <div className="flex flex-col gap-4" data-testid="caption-visible-region">
        {recentFinalSegments
          .slice()
          .reverse()
          .map((segment) => (
            <VideoMeetingSubtitleItem
              key={segment.id}
              speaker={segment.speakerName}
              text={segment.text}
            />
          ))}
        {livePartialSegment && (
          <VideoMeetingSubtitleItem
            key={livePartialSegment.id}
            speaker={livePartialSegment.speakerName}
            text={livePartialSegment.text}
          />
        )}
      </div>
      <div
        aria-live="polite"
        aria-atomic="false"
        className="sr-only"
        data-testid="caption-live-region"
      >
        {finalSegmentsForSr.map((segment) => (
          <Type as="p" variant="body2" key={segment.id}>
            {segment.speakerName} says: {segment.text}
          </Type>
        ))}
      </div>
    </section>
  );
}
